<template>
  <div class="summary-mode-content" data-testid="summary-mode-content">
    <Tabs
      id="summary-mode-content-tabs"
      :selected-tab="selectedTab"
      @tab-click="tab => (selectedTab = tab as SummaryModeTab)"
      @changed="(e: any) => (selectedTab = e.tab.hash as SummaryModeTab)"
    >
      <Tab id="smart-actions" name="Smart Actions" class="summary-mode-content__tab">
        <div v-if="description" class="summary-mode-content__header">
          <Button
            v-if="!summaryContentRef?.editMode"
            type="link"
            variant="gray"
            icon="general.edited"
            tooltip="Edit content"
            data-testid="summary-edit-btn"
            @click="summaryContentRef?.enterEditMode()"
          />
          <Button
            v-if="!summaryContentRef?.editMode"
            type="solid"
            variant="blue"
            icon="general.generalExport"
            text="Export"
            data-testid="summary-export-btn"
            @click="openExportModal"
          />
        </div>
        <SummaryContent ref="summaryContentRef" />
        <div v-if="showFeedback && !summaryContentRef?.editMode" class="summary-mode-content__feedback">
          <span class="summary-mode-content__feedback-label">Was this AI-generated content accurate?</span>
          <Feedback
            :feedback="feedbackState"
            :aiProvider="descriptionProvider"
            :additionalTrackingProps="isSmartActionDescription ? { smart_action_type: smartActionType } : {}"
            @feedback="handleFeedback"
          />
        </div>
      </Tab>

      <Tab id="chapters" :hidden="!hasChapters" name="Chapters" class="summary-mode-content__tab">
        <ChaptersTab />
      </Tab>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
type SummaryModeTab = '#smart-actions' | '#chapters';

import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { Tab, Tabs, Button } from '@/storybook';
import Feedback from '@/storybook/templates/smart_actions/Feedback.vue';

import SummaryContent from '@/modules/viewer/components/SummaryContent.vue';
import ChaptersTab from '@/modules/viewer/components/ChaptersTab.vue';

import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useAiMetadataStore } from '@/modules/viewer/store/useAiMetadataStore';
import { useSummaryFeedback } from '@/modules/viewer/composables/useSummaryFeedback';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';

const { description } = storeToRefs(useViewerItemStore());
const { hasChapters } = storeToRefs(useAiMetadataStore());
const { openShareModalAction } = useItemActions();
const { descriptionProvider, feedbackState, handleFeedback, isSmartActionDescription, showFeedback, smartActionType } = useSummaryFeedback();

const summaryContentRef = ref<InstanceType<typeof SummaryContent> | null>(null);
const selectedTab = ref<SummaryModeTab>('#smart-actions');

const openExportModal = () => {
  openShareModalAction({ modalProps: { selectedTab: '#export' } });
};
</script>

<style scoped>
.summary-mode-content {
  @apply flex flex-col w-full;
}

.summary-mode-content__tab {
  @apply relative flex flex-col p-3 sm:p-4 border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 rounded-10;
}

.summary-mode-content__header {
  @apply flex items-center justify-between gap-2 -m-2 p-2 mb-2 bg-white dark:bg-zinc-900 border-b border-slate-300 dark:border-zinc-600;
}

.summary-mode-content__feedback {
  @apply flex items-center justify-between gap-2 mt-3 pt-3 border-t border-slate-200 dark:border-zinc-700;
}

.summary-mode-content__feedback-label {
  @apply text-sm text-zinc-600 dark:text-zinc-400;
}
</style>
